import React, { Fragment } from "react";
import { PenToolIcon, Sparkles, Calculator, Download, Copy, Trash, ScanText, Megaphone, Check } from "lucide-react";
import { useFileDetailModal } from "../context/FileDetailModalContext";
import { isImageFileTyped } from "@/react/components/entityViews/files/components/FileHelpers";
import { getFileExtension, cn } from "@/modern/utils";
import { getPresignedImageUrlForFile } from "@/network/network";
import { toast } from "sonner";
import { useConfirm } from "@omit/react-confirm-dialog";
import { Button } from "@/modern/components/ui/button-shadcn";

export const Toolbar = () => {
  const { file, handleAnnotating, handleCreateSpotIncentive, shouldShowSpotIncentiveButton, handleFileDownload, setIsAnalyzeDialogOpen, deleteFile, setOpen, isExtractionPanelOpen, setIsExtractionPanelOpen, extractionStates, isExtractable, isPromotePanelOpen, setIsPromotePanelOpen, isFilePromoted, handleCloseExtractionPanel } = useFileDetailModal();
  const confirm = useConfirm();

  if (!file) return null;

  const fileExt = getFileExtension(file.name);
  const isPDF = fileExt === "pdf";
  const isImage = isImageFileTyped(file) && !isPDF;

  const handlePromoteClick = async () => {
    if (file.isCCImage) {
      await confirm({
        title: "Promote Unavailable",
        description: "Promote is not available for CompanyCam images.",
        customActions: ({ confirm }: { confirm: () => void }) => (
          <div>
            <Button onClick={confirm}>OK</Button>
          </div>
        )
      });
      return;
    }

    if (isExtractionPanelOpen) {
      handleCloseExtractionPanel();
    }
    setIsPromotePanelOpen(!isPromotePanelOpen);
  };

  const handleCopyImage = async () => {
    if (!navigator.clipboard || !isImageFileTyped(file)) {
      return;
    }

    try {
      // Load image with CORS enabled to avoid tainted canvas
      const img = new Image();
      img.crossOrigin = "anonymous";

      const imageLoaded = new Promise((resolve, reject) => {
        img.onload = () => resolve(img);
        img.onerror = reject;
      });

      if (file.isCCImage) {
        img.src = file.originalImageUrl || "";
      } else {
        const response = await getPresignedImageUrlForFile(file.id);
        img.src = response.data.url;
      }
      await imageLoaded;

      // Convert image to PNG using canvas (PNG has better clipboard support)
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        throw new Error("Failed to get canvas context");
      }

      ctx.drawImage(img, 0, 0);

      // Convert canvas to blob
      const pngBlob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, "image/png");
      });

      if (!pngBlob) {
        throw new Error("Failed to convert canvas to blob");
      }

      // Write PNG image to clipboard
      await navigator.clipboard.write([
        new ClipboardItem({
          "image/png": pngBlob
        })
      ]);

      // Show success feedback
      toast.success("Image copied to clipboard!");
    } catch (error) {
      console.error("Error copying image:", error);
      toast.error("Unable to copy image to clipboard. Please try downloading instead.");
    }
  };

  // Build array of button elements to render
  const buttons: Array<{ element: React.ReactNode; key: string }> = [];

  if (isImage) {
    buttons.push({
      key: "annotate",
      element: (
        <button type="button" className={cn("flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 rounded-md hover:bg-background/60 active:bg-background/70 transition-all duration-150")} onClick={handleAnnotating}>
          <PenToolIcon className="w-4 h-4" />
          <span className="text-xs font-medium">Annotate</span>
        </button>
      )
    });

    buttons.push({
      key: "analyze",
      element: (
        <button
          type="button"
          className={cn("flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 rounded-md hover:bg-background/60 active:bg-background/70 transition-all duration-150")}
          onClick={() => setIsAnalyzeDialogOpen(true)}
        >
          <Sparkles className="w-4 h-4" />
          <span className="text-xs font-medium">Analyze</span>
        </button>
      )
    });

    buttons.push({
      key: "promote",
      element: (
        <button
          type="button"
          className={cn(
            "relative flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 rounded-md transition-all duration-150",
            "hover:bg-background/60 active:bg-background/70",
            isPromotePanelOpen && "bg-primary/10 text-primary",
            isFilePromoted && !isPromotePanelOpen && "text-emerald-500"
          )}
          onClick={handlePromoteClick}
        >
          <Megaphone className="w-4 h-4" />
          <span className="text-xs font-medium">Promote</span>
          {isFilePromoted && (
            <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500 text-white">
              <Check className="w-2.5 h-2.5" />
            </span>
          )}
        </button>
      )
    });

    if (shouldShowSpotIncentiveButton) {
      buttons.push({
        key: "adjust-points",
        element: (
          <button
            type="button"
            className={cn("flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 rounded-md hover:bg-background/60 active:bg-background/70 transition-all duration-150")}
            onClick={(e) => {
              e.stopPropagation();
              handleCreateSpotIncentive();
            }}
          >
            <Calculator className="w-4 h-4" />
            <span className="text-xs font-medium">Adjust Points</span>
          </button>
        )
      });
    }
  }

  if (isExtractable) {
    const cachedCount = Object.values(extractionStates).filter((s) => !!s?.results).length;

    buttons.push({
      key: "extract",
      element: (
        <button
          type="button"
          className={cn(
            "relative flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 rounded-md hover:bg-background/60 active:bg-background/70 transition-all duration-150",
            isExtractionPanelOpen && "bg-primary/10 text-primary"
          )}
          onClick={() => {
            if (isPromotePanelOpen) {
              setIsPromotePanelOpen(false);
            }
            setIsExtractionPanelOpen(!isExtractionPanelOpen);
          }}
        >
          <ScanText className="w-4 h-4" />
          <span className="text-xs font-medium">Extract</span>
          {cachedCount > 0 && !isExtractionPanelOpen && (
            <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center w-4 h-4 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold leading-none">
              {cachedCount}
            </span>
          )}
        </button>
      )
    });
  }

  buttons.push({
    key: "download",
    element: (
      <button type="button" className={cn("flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 rounded-md hover:bg-background/60 active:bg-background/70 transition-all duration-150")} onClick={() => handleFileDownload(file)}>
        <Download className="w-4 h-4" />
        <span className="text-xs font-medium">Download</span>
      </button>
    )
  });

  if (isImage && "clipboard" in navigator) {
    buttons.push({
      key: "copy",
      element: (
        <button type="button" className={cn("flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 rounded-md hover:bg-background/60 active:bg-background/70 transition-all duration-150")} onClick={handleCopyImage}>
          <Copy className="w-4 h-4" />
          <span className="text-xs font-medium">Copy</span>
        </button>
      )
    });
  }

  if (!file.isCCImage) {
    buttons.push({
      key: "delete",
      element: (
        <button
          type="button"
          className={cn("flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 rounded-md hover:bg-destructive/60 hover:text-destructive-foreground active:bg-destructive/30 transition-all duration-150")}
          onClick={async () => {
            if (
              file.canEdit &&
              (await confirm({
                title: "Confirm Delete",
                description: `Are you sure you want to delete "${file.name}"?`,
                customActions: ({ confirm, cancel }: { confirm: () => void; cancel: () => void }) => (
                  <div className="space-x-4">
                    <Button variant="ghost" onClick={cancel}>
                      Cancel
                    </Button>
                    <Button variant="destructive" onClick={confirm}>
                      Delete
                    </Button>
                  </div>
                )
              }))
            ) {
              deleteFile(file.id);
              setOpen(false);
            }
          }}
        >
          <Trash className="w-4 h-4" />
          <span className="text-xs font-medium">Delete</span>
        </button>
      )
    });
  }

  return (
    <div className="absolute top-3 left-3 z-10">
      <div className="bg-background shadow-2xl rounded-lg px-1 space-y-1.5 py-3 flex flex-col gap-1.5 w-fit">
        {buttons.map((button) => (
          <Fragment key={button.key}>
            {button.element}
          </Fragment>
        ))}
      </div>
    </div>
  );
};
